import { Dispatch, SetStateAction } from "react";
import Box from "@mui/material/Box";
import Pagination from "@mui/material/Pagination";
import Typography from "@mui/material/Typography";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

const LIST_LIMIT = [10, 20, 50, 100];

/** A 1-based page from the URL; anything missing or malformed is page 1. */
export const parsePageParam = (value: string | null | undefined): number => {
  const page = Number(value);
  return Number.isInteger(page) && page >= 1 ? page : 1;
};

/** A page size from the URL, accepted only when the control offers it. */
export const parseLimitParam = (
  value: string | null | undefined,
  fallback: number,
): number => {
  const limit = Number(value);
  return LIST_LIMIT.includes(limit) ? limit : fallback;
};

/**
 * The page to show for `count` rows: the requested one, or the last page that has rows when
 * the request ran past the end (a row acted on was the only one on the last page, or a stale
 * link). Never below 1.
 */
export const clampPage = (page: number, count: number, limit: number): number => {
  const lastPage = Math.max(1, Math.ceil(count / Math.max(limit, 1)));
  return Math.min(Math.max(page, 1), lastPage);
};

interface CustomerPaginationProps {
  totalCount: number;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  limit: number;
  setLimit: Dispatch<SetStateAction<number>>;
}

export default function ETaxPagination({
  totalCount,
  page,
  setPage,
  limit,
  setLimit,
}: CustomerPaginationProps) {
  const handleChangeLimit = (event: SelectChangeEvent<number>) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{ p: 2, [compact]: { p: 1.5, flexWrap: "wrap", gap: 1 } }}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Typography variant="body2">แสดง</Typography>
        <Select size="small" value={limit} onChange={handleChangeLimit}>
          {LIST_LIMIT.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="body2">รายการ</Typography>
      </Box>
      <Pagination
        count={Math.ceil(totalCount / limit)}
        page={page}
        onChange={(_, newPage) => setPage(newPage)}
        color="primary"
        variant="outlined"
        shape="rounded"
      />
    </Box>
  );
}
